import { Component, Injector, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ThemesServiceV2 } from '@core/themes/themes.service';
import { environment } from 'environments/environment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { SettingsService } from '../../core/settings/settings.service';
import { LoginService } from './login.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnDestroy {
  public errMsg: string;
  public successMsg: string;
  public rememberme: boolean;
  public isLoadingLogin = false;
  public loginForm: FormGroup;

  private _getMfaSettingsSubject$ = new Subject();
  private _loginSubject$ = new Subject();
  // list of urls that do not require multi factor authentication
  private _skipMfaUrls: Array<string> = [
    '/settings/forms',
    '/settings/forms/definitions'
  ]

  constructor(
    private loginsvc: LoginService,
    private router: Router,
    private fb: FormBuilder,
    public settings: SettingsService,
    public injector: Injector,
    private themeServiceV2: ThemesServiceV2
  ) {
    this.initForm();
  }

  ngOnDestroy() {
    this._getMfaSettingsSubject$.next();
    this._getMfaSettingsSubject$.unsubscribe();

    this._loginSubject$.next();
    this._loginSubject$.unsubscribe();
  }

  /**
   * toggle remember me checkbox
   */
  remember(): void {
    this.rememberme = !this.rememberme;
  }

  /**
   * log in
   */
  login(): void {
    this.isLoadingLogin = true;
    this.errMsg = undefined;

    const { username, password, dealerId } = this.loginForm.getRawValue();
    this.loginsvc.login(username, password, dealerId)
      .pipe(takeUntil(this._loginSubject$))
      .subscribe(
        data => {
          if (data.IsSuccess) {
            this.successMsg = 'Login Successful';
            if (this.rememberme) {
              localStorage.setItem('userlogin', username);
              localStorage.setItem('userpwd', password);
              localStorage.setItem('dealerId', dealerId);
            }

            const url = this.router.routerState.snapshot.root.queryParams['next'];

            if (!this.skipMfa(url)) {
              this.loginsvc.getMfaSettings()
                .pipe(takeUntil(this._getMfaSettingsSubject$))
                .subscribe(result => {
                  if (result.isDealershipMfaEnabled) {
                    if (!result.isEmployeeMfaEnabled) {
                      this.router.navigate(['/mfasetup'], { queryParams: { next: url } });
                    } else {
                      this.router.navigate(['/mfaauthorize'], { queryParams: { next: url } });
                    }
                  } else {
                    this.themeServiceV2.initTheme();
                    if (url) {
                      this.router.navigateByUrl(url);
                    } else {
                      this.router.navigate([environment.defaultRouteAfterLogin]);
                    }
                  }
                })
            } else {
              this.router.navigateByUrl(url);
            }

          } else {
            this.errMsg = 'Login Failed: ' + data.Message;
            this.isLoadingLogin = false;
          }
        },
        err => {
          this.isLoadingLogin = false;
          if (err && err.error) {
            const msg = err.error;
            if (msg.login_failure) {
              this.errMsg = msg.login_failure;
            } else {
              this.errMsg = 'Login Failed';
            }
          }
        }
      );
  }

  /**
   * log out
   */
  logout(): void {
    this.loginsvc.logout();
    if (this.rememberme === false) {
      localStorage.removeItem('userlogin');
      localStorage.removeItem('userpwd');
    }
    this.router.navigate([environment.loginRoute]);
  }

  /**
   * check if mfa step has to be skipped
   * @param url redirect url
   * @returns true if mfa step has to be skipped otherwise false
   */
  private skipMfa(url: string): boolean {
    if (url) {
        const omitMfa = this._skipMfaUrls.find(x => x == url);
        
        if (omitMfa) {
          return true;
        }
    }

    return false;
  }

  /**
   * init form
   */
  private initForm(): void {
    let username = '';
    let password = '';
    let dealerId = '';

    // --------------- the following is for testing only ----------
    if (localStorage.getItem('userlogin') && localStorage.getItem('userpwd')) {
      username = localStorage.getItem('userlogin');
      password = localStorage.getItem('userpwd');
    } else {
      this.rememberme = false;
    }

    if (localStorage.getItem('dealerId')) {
      dealerId = localStorage.getItem('dealerId');
    }

    this.loginForm = this.fb.group({
      username: [username, Validators.required],
      password: [password, Validators.required],
      dealerId: [
        dealerId,
        [
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(10)
        ]
      ]
    });
  }

}
